// src/components/Navbar.jsx — v11
// CHANGED FROM v10 (31 Jul 2026): two real fixes.
// 1) REAL BUG FIX — selecting "All" never highlighted like Women/Men did.
//    Root cause: handleGenderSelect() called localStorage.setItem(KEY, null),
//    but localStorage only stores strings, so JS silently coerced null into
//    the literal string "null". currentGender's comparison was against real
//    null, which never matched the stored string "null" — so it always
//    failed. Now uses localStorage.removeItem() when gender is null instead
//    of ever storing the string "null".
// 2) Removed the search icon from the top nav — it's already present in
//    BottomNav.jsx, so this was a duplicate. Freed up enough room to bring
//    the gender toggle back onto the same single row as the logo and
//    remaining icons (wallet/cart/account), with the logo kept at its
//    original full size — no shrinking needed once search was dropped.
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../context/StoreContext';

const TIER_COLORS = {
  'Just Arrived': '#9CA3AF',
  'Getting Comfy': '#2F6FED',
  'Part of the Gang': '#2F6FED',
  'Inner Circle': '#D4A020',
  'nobrand OG': '#D4A020',
};

const ADMIN_EMAIL = 'nobrandstore3@gmail.com';
const GENDER_STORAGE_KEY = 'nbs_gender';

export default function Navbar({ title, showBack = false, onBack }) {
  const { cartCount, user } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const wallet = user?.wallet;
  const isAdmin = user?.email === ADMIN_EMAIL;
  const tierColor = wallet ? (TIER_COLORS[wallet.tier] || '#9CA3AF') : '#9CA3AF';

  const currentGender = (() => {
    if (location.pathname === '/women') return 'women';
    if (location.pathname === '/men') return 'men';
    try {
      return localStorage.getItem(GENDER_STORAGE_KEY) || null;
    } catch (e) {
      return null;
    }
  })();

  function handleGenderSelect(gender) {
    try {
      if (gender) {
        localStorage.setItem(GENDER_STORAGE_KEY, gender);
      } else {
        // FIXED (v11): removeItem, not setItem(key, null) — storing null
        // silently became the string "null", which never matched the
        // real `null` currentGender compares against, so "All" could
        // never show as active.
        localStorage.removeItem(GENDER_STORAGE_KEY);
      }
    } catch (e) {
      // localStorage unavailable, proceed without persisting
    }
    if (gender === 'women') navigate('/women');
    else if (gender === 'men') navigate('/men');
    else navigate('/shop');
  }

  const Logo = () => (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 1 }}>
        <span style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 28,
          fontWeight: 600,
          color: '#F4F2EE',
          letterSpacing: '0.02em',
          lineHeight: 1,
        }}>nobrand</span>
        <span style={{
          fontFamily: "'DM Sans', Arial, sans-serif",
          fontSize: 13,
          fontWeight: 400,
          color: '#D4A020',
          letterSpacing: '0.18em',
          lineHeight: 1,
        }}>store</span>
      </div>
      <div style={{ height: 1, background: '#D4A020', width: '100%', margin: '0 0 5px' }} />
      <span style={{
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        fontSize: 11,
        fontWeight: 400,
        fontStyle: 'italic',
        color: 'rgba(244,242,238,0.55)',
        letterSpacing: '0.18em',
        whiteSpace: 'nowrap',
      }}>dressed. not branded.</span>
    </div>
  );

  function handleBackClick() {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  }

  const GenderToggle = () => (
    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
      {[
        { key: 'women', label: 'Women' },
        { key: 'men', label: 'Men' },
        { key: null, label: 'All' },
      ].map((opt) => {
        const active = currentGender === opt.key;
        return (
          <button
            key={opt.label}
            className="nav-gender-btn"
            onClick={() => handleGenderSelect(opt.key)}
            aria-pressed={active}
            style={{
              background: active ? '#D4A020' : 'transparent',
              color: active ? '#1C1A17' : '#F4F2EE',
              border: active ? 'none' : '0.5px solid rgba(244,242,238,0.3)',
              borderRadius: 6,
              padding: '5px 9px',
              fontSize: 11,
              fontWeight: active ? 500 : 400,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="nav-wrap">
      <nav className="top-nav">
        {showBack ? (
          <button className="nav-icon" onClick={handleBackClick} aria-label="Go back">
            <i className="ti ti-arrow-left"></i>
          </button>
        ) : (
          <div className="nav-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer', flexShrink: 0 }}>
            <Logo />
          </div>
        )}

        {!showBack && <GenderToggle />}

        {title && showBack && (
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--off-white)', letterSpacing: '0.08em' }}>
            {title}
          </span>
        )}

        <div className="nav-actions" style={{ flexShrink: 0 }}>
          {!showBack && isAdmin && (
            <button className="nav-icon" onClick={() => navigate('/amitspanel-admin')} aria-label="Admin Panel">
              <i className="ti ti-shield-lock" style={{ color: '#D4A020' }}></i>
            </button>
          )}
          {!showBack && wallet && (
            <button className="nav-icon" onClick={() => navigate('/account')} aria-label="nb wallet">
              <i className="ti ti-wallet" style={{ color: tierColor }}></i>
              <span className="cart-badge" style={{ background: tierColor }}>
                ₹{wallet.walletBalance >= 1000 ? Math.round(wallet.walletBalance / 1000) + 'k' : wallet.walletBalance}
              </span>
            </button>
          )}
          <button className="nav-icon" onClick={() => navigate('/cart')} aria-label="Cart">
            <i className="ti ti-shopping-bag"></i>
            {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
          </button>
          {!showBack && (
            <button className="nav-icon" onClick={() => navigate('/account')} aria-label="Account">
              <i className="ti ti-user"></i>
            </button>
          )}
        </div>
      </nav>
    </div>
  );
}